import { ChevronDown, Code, Database, Layers } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { FaCss3Alt, FaFigma, FaGitAlt, FaHtml5, FaPhp, FaReact, FaSymfony } from 'react-icons/fa6';
import { SiTailwindcss, SiNextdotjs, SiCapacitor, SiDirectus, SiJavascript, SiJira, SiPhpstorm, SiStimulus } from 'react-icons/si';
import { useRef } from 'react';

const skills = [
  {
    icon: <Code size={28} />,
    title: 'Frontend',
    items: ['JavaScript', 'React', 'Next.js', 'HTML', 'CSS', 'Tailwind CSS', 'StimulusJS', 'Ionic/Capacitor'],
  },
  {
    icon: <Database size={28} />,
    title: 'Backend',
    items: ['PHP', 'Symfony', 'SQL', 'API Platform', 'Directus'],
  },
  {
    icon: <Layers size={28} />,
    title: 'Tools',
    items: ['Visual Studio Code', 'PhpStorm', 'Git', 'ChatGPT', 'Jira', 'Figma'],
  },
];

const iconMap = [
  { name: 'JavaScript', icon: <SiJavascript className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#f7df1e] hover:scale-110" /> },
  { name: 'React', icon: <FaReact className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#61dafb] hover:scale-110" /> },
  { name: 'Next.js', icon: <SiNextdotjs className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-black dark:hover:text-white hover:scale-110" /> },
  { name: 'HTML5', icon: <FaHtml5 className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#e34f26] hover:scale-110" /> },
  { name: 'CSS3', icon: <FaCss3Alt className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#1572b6] hover:scale-110" /> },
  { name: 'Tailwind CSS', icon: <SiTailwindcss className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#38bdf8] hover:scale-110" /> },
  { name: 'StimulusJS', icon: <SiStimulus className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#e53e3e] hover:scale-110" /> },
  { name: 'Capacitor', icon: <SiCapacitor className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#119eff] hover:scale-110" /> },
  { name: 'PHP', icon: <FaPhp className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#777bb4] hover:scale-110" /> },
  { name: 'Symfony', icon: <FaSymfony className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-black dark:hover:text-white hover:scale-110" /> },
  { name: 'Directus', icon: <SiDirectus className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-black dark:hover:text-white hover:scale-110" /> },
  { name: 'PhpStorm', icon: <SiPhpstorm className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#9a1bde] hover:scale-110" /> },
  { name: 'Git', icon: <FaGitAlt className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#f05032] hover:scale-110" /> },
  { name: 'Jira', icon: <SiJira className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#0052cc] hover:scale-110" /> },
  { name: 'Figma', icon: <FaFigma className="h-10 w-10 text-black dark:text-white transition duration-300 hover:text-[#f24e1e] hover:scale-110" /> },
];



const Skills = ({scrollTo, projectsRef}: any) => {

  const iconRef = useRef(null);
  const isInView = useInView(iconRef, { once: true });

  return (
    <section className="h-auto relative md:h-screen min-h-screen md:snap-start flex items-center justify-center px-6 md:px-20 text-gray-900 dark:text-white">
      <div className="max-w-6xl w-full">
        <motion.h2
          className="text-5xl md:text-6xl ralewaytitle mb-12 !italic mt-16 md:mt-0 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Skills
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {skills.map((skill, index) => (
            <motion.div
              key={index}
              className="rounded-xl shadow-lg overflow-hidden bg-gray-100 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 transition"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center gap-2 p-3 bg-gray-200 dark:bg-gray-800">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-auto text-base text-gray-500 dark:text-gray-400 font-mono">
                  {skill.title.toLowerCase()}{index == 0 ? '.js' : index == 1 ? '.php' : '.exe'}
                </span>
              </div>
              <pre className="text-base font-mono p-4 whitespace-pre-wrap leading-relaxed bg-transparent text-left font-bold">
                <code>
                  <span className="text-purple-600 dark:text-purple-400">// {skill.title}</span>
                  {'\n'}
                  <span className="text-pink-600 dark:text-pink-400">const</span>{' '}
                  <span className="text-blue-600 dark:text-blue-400">
                    {skill.title.toLowerCase()}
                  </span>{' '}
                  <span className="text-black dark:text-white">= [</span>
                  {'\n'}
                  {skill.items.map((item, i) => (
                    <span key={item}>
                      {'  '}
                      <span className="text-green-600 dark:text-green-400">
                        "{item}"
                      </span>
                      {i < skill.items.length - 1 ? ',' : ''}
                      {'\n'}
                    </span>
                  ))}
                  <span className="text-black dark:text-white">];</span>
                </code>
              </pre>
            </motion.div>
          ))}
        </div>
        <div ref={iconRef} className="flex flex-grow flex-wrap gap-1 mt-4 justify-center">
        {iconMap.map((icon, index) => (
            <motion.div
              key={index}
              title={icon.name}
              className=""
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
            >
              {icon.icon}
            </motion.div>
            ))}
        </div>
      </div>
      <div onClick={()=>scrollTo(projectsRef)} className="absolute hidden bottom-8 inset-x-0 md:flex justify-center animate-bounce cursor-pointer z-50">
        <ChevronDown size={32} />
      </div>
    </section>
  );
};

export default Skills;

